import { BackInStockToast } from "@faire/deprecated-retailer-visitor-shared/features/BackInStock/BackInStockToast";
import { PerStyleVariationMapBuilder } from "@faire/deprecated-retailer-visitor-shared/features/OpenSizing/PerStyleVariationMapBuilder";
import {
  getCurrentStyleHasOnlyPrePacks,
  getStyleKeyFromVariations,
} from "@faire/deprecated-retailer-visitor-shared/features/PrePacks/addToCartUtils";
import { isPrePackProductOption as isPrePackProductOptionFn } from "@faire/deprecated-retailer-visitor-shared/features/PrePacks/isPrePackProduct";
import { IProductAddOptions } from "@faire/deprecated-retailer-visitor-shared/features/ProductAdd/ProductAddOptions";
import { getOptionAvailableUnits } from "@faire/deprecated-retailer-visitor-shared/lib/product";
import { getInitialProductOptionWithCustomization } from "@faire/deprecated-retailer-visitor-shared/lib/productOptions";
import { stringOrUndefined } from "@faire/deprecated-retailer-visitor-shared/lib/stringOrUndefined";
import { getCustomizationsMapFromItemCustomizations } from "@faire/deprecated-retailer-visitor-shared/services/ItemCustomization";
import {
  getAppliedProductCustomizations,
  getAppliedProductCustomizationsAdditionalCharge,
  getProductCustomizationsMinimumOrderQuantity,
} from "@faire/deprecated-retailer-visitor-shared/services/ProductCustomization";
import {
  ICartItemForUpdate,
  ILeanCartItem,
} from "@faire/deprecated-retailer-visitor-shared/stores/domain/CartStore/consts";
import { AddToCartVariants } from "@faire/deprecated-retailer-visitor-shared/stores/domain/CartStore/consts";
import { useEventSubscriptionStore } from "@faire/deprecated-retailer-visitor-shared/stores/domain/EventSubscriptionStore";
import { NUMBER_OF_QUANTITY_OPTIONS } from "@faire/deprecated-retailer-visitor-shared/views/product/consts";
import {
  getProductDetails,
  getSortedProductOptions,
} from "@faire/deprecated-retailer-visitor-shared/views/product/utils/productUtils";
import { isNotUndefined } from "@faire/typescript";
import { useInvalidateQuery as useInvalidatePromotionsQuery } from "@faire/web-api--source/endpoints/www/api/product/productToken/product-promo-messages/get-hooks";
import { trackCartAddToCartWebCujClickTraceUpdateCartApiCallComplete } from "@faire/web-api--source/events/cart/click/addToCartWebCuj";
import { IComputeExpectedAddToCartAmountRequest } from "@faire/web-api--source/faire/carts/IComputeExpectedAddToCartAmountRequest";
import { ICartItem } from "@faire/web-api--source/indigofair/data/ICartItem";
import { IFaireMoney } from "@faire/web-api--source/indigofair/data/IFaireMoney";
import { IItemCustomization } from "@faire/web-api--source/indigofair/data/IItemCustomization";
import { IProductOption } from "@faire/web-api--source/indigofair/data/IProductOption";
import { IProductPageResponse } from "@faire/web-api--source/indigofair/data/IProductPageResponse";
import { ProductVariationType } from "@faire/web-api--source/indigofair/data/ProductVariationType";
import { FaireDate } from "@faire/web/common/FaireDate";
import { FaireDateRange } from "@faire/web/common/FaireDateRange";
import {
  ErrorMessages,
  getErrorMessage,
} from "@faire/web/common/getErrorMessage";
import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { useStrictLocalization } from "@faire/web/common/localization";
import { MoneyMath } from "@faire/web/common/MoneyMath";
import { createToast } from "@faire/web/slate/Toast/Toast";
import find from "lodash/find";
import groupBy from "lodash/groupBy";
import isEqual from "lodash/isEqual";
import isEqualWith from "lodash/isEqualWith";
import qs from "query-string";
import React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useHistory } from "react-router-dom";

export type ProductAddToCartState = ReturnType<typeof useProductAddToCartState>;

export const useProductAddToCartState = (
  product: IProductPageResponse.IPdpProduct,
  brandToken: string | undefined,
  productAddOptions: IProductAddOptions,
  fetchProductPromotions?: boolean,
  quantityMap?: Map<string, number>,
  defaults?: {
    quantity?: number;
    optionToken?: string;
    customizations?: IItemCustomization[];
  },
  // Can be passed in cases where we know the token earlier than when the product is loaded
  productToken?: string | undefined,
  cartItemToken?: string,
  updateOnChange?: boolean
) => {
  const history = useHistory();
  const { strictLocalize } = useStrictLocalization();

  const isOptionPartOfOpenSizing = useCallback(
    (productOption?: IProductOption) => {
      const sizeVariation =
        product.purchase_information?.variation_map?.entries?.find(
          (entry) => entry.type === ProductVariationType.SIZE
        );

      // we check for selectedProductOption because it will be undefined
      // in certain cases for around prepacks.
      return !!(
        sizeVariation &&
        product.purchase_information?.per_style_min_order_quantity &&
        product.purchase_information?.per_style_min_order_quantity > 0 &&
        productOption &&
        !isPrePackProductOptionFn(productOption)
      );
    },
    [product]
  );

  const productOptions = useMemo(
    () => getSortedProductOptions(product),
    [product]
  );
  const defaultProductOption = useMemo(
    () =>
      defaults?.optionToken
        ? productOptions.find((option) => option.token === defaults.optionToken)
        : undefined,
    [defaults?.optionToken, productOptions]
  );
  const {
    findByToken: findByTokenQueryParam,
    updateQuantityOnChange: updateQuantityOnChangeQueryParam,
  } = useMemo(
    () => qs.parse(history.location.search),
    [history.location.search]
  );

  const findByToken = useMemo(
    () => cartItemToken ?? findByTokenQueryParam,
    [cartItemToken, findByTokenQueryParam]
  );

  const defaultCustomizations = useMemo(
    () =>
      defaults?.customizations
        ? getCustomizationsMapFromItemCustomizations(defaults.customizations)
        : undefined,
    [defaults?.customizations]
  );
  const { initialProductOption, customizations: initialCustomizations } =
    useMemo(
      () =>
        getInitialProductOptionWithCustomization(
          productOptions,
          stringOrUndefined(findByToken) ?? "",
          productAddOptions.getAddedItemByToken
        ),
      [findByToken, productAddOptions.getAddedItemByToken, productOptions]
    );
  const [selectedOption, setSelectedOption] = useState<
    IProductOption | undefined
  >(defaultProductOption ?? initialProductOption);

  const unitMultiplier = useMemo(() => {
    return (
      selectedOption?.unit_multiplier ||
      product.purchase_information?.unit_multiplier ||
      1
    );
  }, [
    selectedOption?.unit_multiplier,
    product.purchase_information?.unit_multiplier,
  ]);

  const maxAvailableUnits = useMemo(() => {
    const availableUnits = getOptionAvailableUnits(
      getProductDetails(product),
      selectedOption
    );

    if (availableUnits === Infinity) {
      return Infinity;
    }

    return availableUnits > unitMultiplier
      ? availableUnits - (availableUnits % unitMultiplier)
      : availableUnits;
  }, [product, selectedOption, unitMultiplier]);

  const [customizations, setCustomizations] = useState(
    () =>
      defaultCustomizations ??
      initialCustomizations ??
      new Map<string, string>()
  );
  const [pendingCustomizations, setPendingCustomizations] = useState<
    Set<IItemCustomization>
  >(
    () =>
      new Set(
        Object.entries(customizations).map(([token, value]) =>
          IItemCustomization.build({
            product_customization_token: token,
            value,
            type: "TEXT",
          })
        )
      )
  );

  const isPrePackProductOption = useMemo(() => {
    return !!selectedOption && isPrePackProductOptionFn(selectedOption);
  }, [selectedOption]);

  const getOptionInCart = useCallback(
    (
      optionToken: string,
      customization: Map<string, string>
    ): ILeanCartItem | undefined =>
      productAddOptions.getAddedItemByProductOptionTokenAndCustomizations(
        optionToken,
        customization
      ),
    [productAddOptions]
  );

  const getSelectedOptionInCart = useCallback(() => {
    return selectedOption?.token
      ? getOptionInCart(selectedOption.token, customizations)
      : undefined;
  }, [customizations, getOptionInCart, selectedOption?.token]);

  const minimumOrderQuantity = useMemo(() => {
    // TODO(prepacks): If min_order_quantity was added to product option we could remove this code
    if (isPrePackProductOption) {
      return 1;
    }

    const appliedCustomizations = getAppliedProductCustomizations(
      product.purchase_information?.customizations || [],
      customizations
    );
    if (appliedCustomizations.length > 0) {
      const minimumQuantityForAppliedCustomizations =
        getProductCustomizationsMinimumOrderQuantity(appliedCustomizations);

      if (minimumQuantityForAppliedCustomizations > 0) {
        return minimumQuantityForAppliedCustomizations;
      }
    }

    const perStyleMOQ =
      product.purchase_information?.per_style_min_order_quantity;

    return perStyleMOQ && perStyleMOQ > 0
      ? perStyleMOQ
      : product.purchase_information?.min_order_quantity || unitMultiplier;
  }, [
    customizations,
    isPrePackProductOption,
    product.purchase_information?.customizations,
    product.purchase_information?.min_order_quantity,
    product.purchase_information?.per_style_min_order_quantity,
    unitMultiplier,
  ]);

  const updateQuantityOnChange =
    updateOnChange ?? updateQuantityOnChangeQueryParam === "true";

  const minimumSelectableQuantity = useMemo(() => {
    const selectedOptionInCart = getSelectedOptionInCart();
    if (updateQuantityOnChange) {
      return Math.max(minimumOrderQuantity, unitMultiplier);
    } else {
      return Math.max(
        minimumOrderQuantity - (selectedOptionInCart?.quantity || 0),
        unitMultiplier
      );
    }
  }, [
    minimumOrderQuantity,
    getSelectedOptionInCart,
    unitMultiplier,
    updateQuantityOnChange,
  ]);

  const productOptionInCart = useMemo(() => {
    const cartProducts = productAddOptions.addedItems;
    if (!cartProducts?.length) {
      return;
    }
    const selectedOptionInCart = getSelectedOptionInCart();
    /**
     *
     * @param selectedProduct the product opened in the modal from cart
     * @param productInCart iteratively pick products in cart for comparison
     * We compare the selected product to the products in cart and see if we can 'restore'
     * existing quantities instead of adding new quantities.
     * When updating products, it must match all the requirements (i.e. option + customization).
     * if any of the requirements aren't met, it is treated as adding a new product
     */
    const customizer = (
      selectedProduct: ILeanCartItem,
      productInCart: ILeanCartItem
    ): boolean => {
      const selectedCustomization = selectedProduct.customizations;
      const productCustomization = productInCart.customizations;
      const isMatchingProduct =
        productInCart.product_token === selectedOptionInCart?.product_token;
      const isMatchingProductOption =
        productInCart.product_option_token ===
        selectedOptionInCart?.product_option_token;

      if (
        !isMatchingProduct ||
        !isMatchingProductOption ||
        selectedCustomization.length !== productCustomization.length
      ) {
        return false;
      }

      if (selectedCustomization.length > 0) {
        return (
          selectedCustomization[0]?.value === productCustomization[0]?.value
        );
      }

      return true;
    };

    return selectedOptionInCart
      ? find(cartProducts, (product) => {
          return isEqualWith(selectedOptionInCart, product, customizer);
        })
      : undefined;
  }, [productAddOptions.addedItems, getSelectedOptionInCart]);

  /**
   * We check for a match within the current cart here.
   * The check includes product style, sizing, customizations.
   * Since testers are of the product option level, we don't need to
   * check it here.
   */
  const existingCartQuantityForSelectedOption = useMemo(() => {
    // if productOptionInCart is undefined, it means the item doesn't exist in cart yet
    if (productOptionInCart?.quantity && productOptionInCart.quantity > 0) {
      // safety check in case the brand changes the MOQ/stock
      if (productOptionInCart.quantity > maxAvailableUnits) {
        return maxAvailableUnits;
      }
      if (productOptionInCart.quantity < minimumOrderQuantity) {
        return minimumOrderQuantity;
      }
      return productOptionInCart.quantity;
    }

    return minimumSelectableQuantity;
  }, [
    maxAvailableUnits,
    minimumOrderQuantity,
    minimumSelectableQuantity,
    productOptionInCart?.quantity,
  ]);

  const getSelectedOptionQuantity = useCallback(
    (option: IProductOption | undefined) => {
      if (option === undefined || isOptionPartOfOpenSizing(option)) {
        return undefined;
      } else {
        const quantity = quantityMap?.get(option.token ?? "");
        if (quantity !== undefined) {
          if (updateQuantityOnChange && productOptionInCart && quantity === 0) {
            return 0;
          } else {
            return Math.max(
              Math.min(
                Math.floor(maxAvailableUnits / unitMultiplier) * unitMultiplier,
                Math.round(quantity / unitMultiplier) * unitMultiplier
              ),
              minimumSelectableQuantity
            );
          }
        } else {
          const cartQuantity = updateQuantityOnChange
            ? existingCartQuantityForSelectedOption
            : minimumSelectableQuantity;

          return Math.max(cartQuantity, option?.unit_multiplier ?? 1);
        }
      }
    },
    [
      existingCartQuantityForSelectedOption,
      isOptionPartOfOpenSizing,
      maxAvailableUnits,
      minimumSelectableQuantity,
      productOptionInCart,
      quantityMap,
      unitMultiplier,
      updateQuantityOnChange,
    ]
  );
  const {
    BackInStockIdentifiers,
    subscribeBackInStock,
    unsubscribeBackInStock,
  } = useEventSubscriptionStore([
    "BackInStockIdentifiers",
    "subscribeBackInStock",
    "unsubscribeBackInStock",
  ]);

  const [selectedQuantity, setSelectedQuantityState] = useState<number>(
    getSelectedOptionQuantity(defaultProductOption ?? initialProductOption) ?? 0
  );
  const [includesTesters, setIncludesTesters] = useState<boolean>(() => {
    const selectedOptionInCart = getSelectedOptionInCart();
    return selectedOptionInCart
      ? !!selectedOptionInCart.includes_tester
      : false;
  });

  const isSubscribedBackInStock = useMemo(() => {
    const token = selectedOption?.token;
    return token ? !!BackInStockIdentifiers[token] : false;
  }, [selectedOption?.token, BackInStockIdentifiers]);

  const [customizationsInMoqCalculation, setCustomizationsInMoqCalculation] =
    useState<Set<string>>(() => new Set());
  const [hideProductDetails, setHideProductDetails] = useState<boolean>(true);

  const invalidatePromotionsQuery = useInvalidatePromotionsQuery();

  const isPreorderableSelected =
    selectedOption?.orderability_type ===
    IProductOption.OrderabilityType.PREORDER;

  const getNewPerStyleVariationMap = useCallback(
    (selectedProductOption: IProductOption | undefined) => {
      if (!isOptionPartOfOpenSizing(selectedProductOption)) {
        return undefined;
      }

      const newVariationMap = PerStyleVariationMapBuilder.buildVariationMap(
        selectedProductOption!.token!,
        product
      );

      if (updateQuantityOnChange) {
        // due to the nature of open sizing, we cannot consider the size that gets selected
        // when opening the modal. We need to use the variation map to determine (if exists) the product
        // option that is actually in cart. If we find one that matches the customizations, we'll
        // restore that quantity to the modal.
        const optionVariationInCart = newVariationMap?.find((variation) => {
          const token = variation.productOptionToken;
          return !!getOptionInCart(token, customizations);
        });
        if (optionVariationInCart) {
          newVariationMap?.forEach((option) => {
            const token = option.productOptionToken;
            const quantity =
              getOptionInCart(token, customizations)?.quantity ?? 0;
            option.cartQuantity = quantity;
            option.selectedQuantity = quantity;
          });
        }
      }

      const hasOptionVariationMapped = newVariationMap?.some((variation) =>
        quantityMap?.has(variation.productOptionToken)
      );
      if (hasOptionVariationMapped) {
        newVariationMap?.forEach((variation) => {
          variation.selectedQuantity = Math.min(
            variation.inventory,
            quantityMap?.get(variation.productOptionToken) ?? 0
          );
        });
      }

      return newVariationMap;
    },
    [
      customizations,
      getOptionInCart,
      isOptionPartOfOpenSizing,
      product,
      quantityMap,
      updateQuantityOnChange,
    ]
  );

  const [perStyleOptionVariations, setPerStyleOptionVariations] = useState<
    PerStyleVariationMapBuilder.IPerStyleOptionVariation[] | undefined
  >(() =>
    getNewPerStyleVariationMap(defaultProductOption ?? initialProductOption)
  );

  const isOpenSizingVariant = !!perStyleOptionVariations;

  const showPreorderMessaging = useMemo(() => {
    if (perStyleOptionVariations) {
      return perStyleOptionVariations?.some(
        (variation) =>
          variation.orderabilityType ===
          IProductOption.OrderabilityType.PREORDER
      );
    }
    return isPreorderableSelected;
  }, [isPreorderableSelected, perStyleOptionVariations]);

  const areAllOptionQuantitiesValid = useMemo(() => {
    return !!perStyleOptionVariations?.every(
      (variation) => variation.selectedQuantity <= variation.inventory
    );
  }, [perStyleOptionVariations]);

  const openSizingShipDateRange = useMemo(() => {
    if (!perStyleOptionVariations) {
      return;
    }
    const lowerBoundShipDate = FaireDate.fromNormalizedOrLocalEndOfDayTimestamp(
      Math.min(
        ...perStyleOptionVariations
          .map(
            (variation) =>
              variation.expectedShipDate?.startDate.normalizedUtcTimestamp
          )
          .filter(isNotUndefined)
      )
    );
    const upperBoundShipDate = FaireDate.fromNormalizedOrLocalEndOfDayTimestamp(
      Math.max(
        ...perStyleOptionVariations
          .map(
            (variation) =>
              variation.expectedShipDate?.endDate.normalizedUtcTimestamp
          )
          .filter(isNotUndefined)
      )
    );
    if (!lowerBoundShipDate || !upperBoundShipDate) {
      return;
    }
    return new FaireDateRange(lowerBoundShipDate, upperBoundShipDate);
  }, [perStyleOptionVariations]);

  const baseOrderByDate = useMemo(() => {
    if (!perStyleOptionVariations) {
      return FaireDate.fromNormalizedOrLocalEndOfDayTimestamp(
        selectedOption?.stop_selling_at ??
          product.purchase_information?.order_by_date
      );
    }
    return perStyleOptionVariations[0]?.orderByDate;
  }, [
    perStyleOptionVariations,
    product.purchase_information?.order_by_date,
    selectedOption?.stop_selling_at,
  ]);

  const openSizingAllSameDates = useMemo(() => {
    if (!perStyleOptionVariations) {
      return;
    }
    const baseExpectedShipDate = perStyleOptionVariations[0]?.expectedShipDate;
    return perStyleOptionVariations.every((variation) => {
      return (
        isEqual(variation.orderByDate, baseOrderByDate) &&
        isEqual(
          variation.expectedShipDate?.startDate,
          baseExpectedShipDate?.startDate
        ) &&
        isEqual(
          variation.expectedShipDate?.windowDays,
          baseExpectedShipDate?.windowDays
        )
      );
    });
  }, [baseOrderByDate, perStyleOptionVariations]);

  const perStyleTotalQuantity = useMemo(() => {
    return (
      perStyleOptionVariations?.reduce(
        (accumulator, curr) => accumulator + curr.selectedQuantity,
        0
      ) ?? 0
    );
  }, [perStyleOptionVariations]);

  const isSelectedOpenSizingQtyModified = useMemo(() => {
    if (
      selectedOption === undefined ||
      !updateQuantityOnChange ||
      !isOptionPartOfOpenSizing
    ) {
      return false;
    }

    return !!perStyleOptionVariations?.some(
      (variation) => variation.cartQuantity !== variation.selectedQuantity
    );
  }, [
    isOptionPartOfOpenSizing,
    perStyleOptionVariations,
    selectedOption,
    updateQuantityOnChange,
  ]);

  const updateSelectedQuantityByToken = useCallback(
    (token: string, quantity: number) => {
      setPerStyleOptionVariations((prevVariations) => {
        if (!prevVariations) {
          return prevVariations;
        }
        let found = false;
        return prevVariations.map((variation) => {
          if (variation.productOptionToken === token && !found) {
            found = true;
            return { ...variation, selectedQuantity: quantity };
          }
          return variation;
        });
      });
    },
    []
  );

  const getWholesalePrice = useCallback(
    (productOption?: IProductOption) => {
      const wholesalePrice =
        productOption?.wholesale_promo_price?.discounted_item_price ??
        productOption?.wholesale_price;
      const customizationPrice =
        getAppliedProductCustomizationsAdditionalCharge(
          product.purchase_information?.customizations ?? [],
          customizations
        );
      return MoneyMath.sum(wholesalePrice, customizationPrice);
    },
    [product, customizations]
  );

  const getRetailPrice = useCallback((productOption?: IProductOption) => {
    return productOption?.retail_price ?? IFaireMoney.build();
  }, []);

  const updateSelectedOptionQuantity = useCallback((quantity: number) => {
    if (isNaN(quantity)) {
      return;
    }
    setSelectedQuantityState(quantity);
  }, []);

  const buildCustomizations = useCallback(
    (optionInCart: ILeanCartItem | undefined) => {
      const productCustomizations =
        product.purchase_information?.customizations ?? [];

      return (
        optionInCart?.customizations ??
        getAppliedProductCustomizations(
          productCustomizations,
          customizations
        ).map(({ token, type }) =>
          IItemCustomization.build({
            product_customization_token: token,
            value: customizations.get(token!),
            type,
          })
        )
      );
    },
    [customizations, product.purchase_information?.customizations]
  );

  const setSelectedQuantity = useCallback(
    (quantity: number) => {
      if (updateQuantityOnChange && productOptionInCart && quantity === 0) {
        // this will allow a deletion
        updateSelectedOptionQuantity(0);
      } else {
        updateSelectedOptionQuantity(
          Math.max(
            Math.min(
              Math.floor(maxAvailableUnits / unitMultiplier) * unitMultiplier,
              Math.round(quantity / unitMultiplier) * unitMultiplier
            ),
            minimumSelectableQuantity
          )
        );
      }
    },
    [
      maxAvailableUnits,
      minimumSelectableQuantity,
      productOptionInCart,
      unitMultiplier,
      updateQuantityOnChange,
      updateSelectedOptionQuantity,
    ]
  );

  const getIsUnderPerStyleMOQ = useCallback(() => {
    let quantitiesInCart = 0;
    // We need to calculate open sizing fields instead
    perStyleOptionVariations?.forEach((variation) => {
      const optionInCart = getOptionInCart(
        variation.productOptionToken,
        customizations
      );
      const totalOptionQuantity = updateQuantityOnChange
        ? variation.selectedQuantity
        : variation.selectedQuantity +
          (optionInCart?.quantity ? optionInCart.quantity : 0);

      quantitiesInCart += totalOptionQuantity;
    });

    if (quantitiesInCart < minimumOrderQuantity) {
      return true;
    }
    return false;
  }, [
    customizations,
    getOptionInCart,
    minimumOrderQuantity,
    perStyleOptionVariations,
    updateQuantityOnChange,
  ]);

  const addOrUpdateSelectedOptionToInvoice = useCallback(async () => {
    const items: ICartItem[] = [];
    const selectedOptionInCart = getSelectedOptionInCart();

    if (perStyleOptionVariations) {
      if (getIsUnderPerStyleMOQ()) {
        throw new Error(
          "Quantity is under the per style minimum order quantity"
        );
      }
      // We need to calculate open sizing fields instead
      perStyleOptionVariations.forEach((variation) => {
        const optionInCart = getOptionInCart(
          variation.productOptionToken,
          customizations
        );
        const optionCustomizations: IItemCustomization[] =
          buildCustomizations(optionInCart);
        const totalOptionQuantity = updateQuantityOnChange
          ? variation.selectedQuantity
          : variation.selectedQuantity + (optionInCart?.quantity ?? 0);

        items.push(
          ICartItem.build({
            brand_token: brandToken,
            product_token: product.token,
            product_option_token: variation.productOptionToken,
            quantity: totalOptionQuantity,
            customizations: optionCustomizations,
            price: getWholesalePrice(selectedOption),
            retail_price: getRetailPrice(selectedOption),
          })
        );
      });
    } else {
      const optionCustomizations: IItemCustomization[] =
        buildCustomizations(selectedOptionInCart);

      items.push(
        ICartItem.build({
          brand_token: brandToken,
          product_token: product.token,
          product_option_token: selectedOption?.token,
          quantity: updateQuantityOnChange
            ? selectedQuantity
            : selectedQuantity + (selectedOptionInCart?.quantity ?? 0),
          // Only allow a tester to be included if we verify
          // that a tester price is well-defined
          includes_tester:
            product.purchase_information?.tester_price?.amount_cents !==
              undefined && includesTesters,
          tester_price: product.purchase_information?.tester_price,
          customizations: optionCustomizations,
          price: getWholesalePrice(selectedOption),
          retail_price: getRetailPrice(selectedOption),
        })
      );
    }

    await productAddOptions.addOrUpdateItems(items);
  }, [
    brandToken,
    buildCustomizations,
    customizations,
    getOptionInCart,
    getRetailPrice,
    getWholesalePrice,
    includesTesters,
    getIsUnderPerStyleMOQ,
    perStyleOptionVariations,
    product.purchase_information?.tester_price,
    product.token,
    productAddOptions,
    selectedOption,
    getSelectedOptionInCart,
    selectedQuantity,
    updateQuantityOnChange,
  ]);

  const addOrUpdateSelectedOptionToCart = useCallback(
    async (startTime: number, variant: AddToCartVariants) => {
      const items: ICartItemForUpdate[] = [];
      const selectedOptionInCart = getSelectedOptionInCart();
      if (perStyleOptionVariations) {
        if (getIsUnderPerStyleMOQ()) {
          throw new Error(
            "Quantity is under the per style minimum order quantity"
          );
        }
        // We need to calculate open sizing fields instead
        perStyleOptionVariations.forEach((variation) => {
          const optionInCart = getOptionInCart(
            variation.productOptionToken,
            customizations
          );
          const optionCustomizations: IItemCustomization[] =
            buildCustomizations(optionInCart);
          const totalOptionQuantity = updateQuantityOnChange
            ? variation.selectedQuantity
            : variation.selectedQuantity + (optionInCart?.quantity ?? 0);

          items.push(
            ICartItemForUpdate.build({
              brand_token: brandToken,
              product_token: product.token,
              product_option_token: variation.productOptionToken,
              quantity: totalOptionQuantity,
              customizations: optionCustomizations,
            })
          );
        });
      } else {
        const optionCustomizations: IItemCustomization[] =
          buildCustomizations(selectedOptionInCart);

        items.push(
          ICartItemForUpdate.build({
            brand_token: brandToken,
            product_token: product.token,
            product_option_token: selectedOption?.token,
            quantity: updateQuantityOnChange
              ? selectedQuantity
              : selectedQuantity + (selectedOptionInCart?.quantity ?? 0),
            // Only allow a tester to be included if we verify
            // that a tester price is well-defined
            includes_tester:
              product.purchase_information?.tester_price?.amount_cents !==
                undefined && includesTesters,
            tester_price: product.purchase_information?.tester_price,
            customizations: optionCustomizations,
          })
        );
      }

      const upltParams = {
        event: "add-to-cart",
        startTime,
        variant,
      } as const;

      await productAddOptions.addOrUpdateItemsV2?.({
        items: [...items],
        upltParams,
      });

      trackCartAddToCartWebCujClickTraceUpdateCartApiCallComplete();

      const token = productToken ?? product.token;

      if (fetchProductPromotions && token) {
        invalidatePromotionsQuery(token);
      }
    },
    [
      perStyleOptionVariations,
      updateQuantityOnChange,
      fetchProductPromotions,
      productToken,
      getIsUnderPerStyleMOQ,
      getOptionInCart,
      customizations,
      buildCustomizations,
      brandToken,
      product.token,
      product.purchase_information?.tester_price,
      getSelectedOptionInCart,
      selectedOption?.token,
      selectedQuantity,
      includesTesters,
      productAddOptions,
      invalidatePromotionsQuery,
    ]
  );

  const getExpectedAddToCartItems = useCallback(() => {
    const items: IComputeExpectedAddToCartAmountRequest.IAddToCartItem[] = [];
    const optionCustomizations: IItemCustomization[] = [
      ...pendingCustomizations,
    ];
    if (perStyleOptionVariations) {
      // We need to calculate open sizing fields instead
      perStyleOptionVariations.forEach((variation) => {
        items.push(
          IComputeExpectedAddToCartAmountRequest.IAddToCartItem.build({
            product_option_token: variation.productOptionToken,
            quantity: variation.selectedQuantity,
            customizations: optionCustomizations,
          })
        );
      });
    } else if (selectedOption?.token) {
      // When the selected option is a prepack, the token is a product collection token (pc_token)
      // Otherwise, it is product option token (po_token)
      const tokenProps =
        selectedOption.type === "PRE_PACK"
          ? { product_collection_token: selectedOption.token }
          : { product_option_token: selectedOption.token };

      items.push(
        IComputeExpectedAddToCartAmountRequest.IAddToCartItem.build({
          quantity: selectedQuantity,
          // Only allow a tester to be included if we verify
          // that a tester price is well-defined
          includes_tester:
            product.purchase_information?.tester_price?.amount_cents !==
              undefined && includesTesters,
          customizations: optionCustomizations,
          ...tokenProps,
        })
      );
    }

    return items;
  }, [
    pendingCustomizations,
    perStyleOptionVariations,
    selectedOption?.token,
    selectedOption?.type,
    selectedQuantity,
    product.purchase_information?.tester_price?.amount_cents,
    includesTesters,
  ]);

  const updateSelectedOption = useCallback(
    (option: IProductOption | undefined) => {
      setSelectedOption(option);
      const newPerStyleVariationMap = getNewPerStyleVariationMap(option);
      setPerStyleOptionVariations(newPerStyleVariationMap);
      const quantity = getSelectedOptionQuantity(option);
      const selectedOptionInCart = getSelectedOptionInCart();
      if (quantity !== undefined) {
        updateSelectedOptionQuantity(quantity);
      }
      // if the selected product option is already in the retailer's
      // cart, then toggle tester checkbox to match if a tester is in their cart
      setIncludesTesters(
        selectedOptionInCart ? !!selectedOptionInCart.includes_tester : false
      );
    },
    [
      getNewPerStyleVariationMap,
      getSelectedOptionQuantity,
      getSelectedOptionInCart,
      updateSelectedOptionQuantity,
    ]
  );

  const debouncedCustomizationOnChange = useMemo(
    () => debounceIfPossible(() => updateSelectedOption(selectedOption), 300),
    [updateSelectedOption, selectedOption]
  );

  const productOptionQuantityLabel = useCallback(
    (quantity: number) => {
      // when selected product in cart is no longer available
      if (!selectedOption?.wholesale_price) {
        return "0";
      }
      return `${quantity != null ? quantity.toString() : "0"}`;
    },
    [selectedOption]
  );

  const selectedOptionLabel = useMemo(() => {
    return productOptionQuantityLabel(selectedQuantity ?? 0);
  }, [productOptionQuantityLabel, selectedQuantity]);

  const getCustomOptionValue = useCallback(
    (quantity: number) => {
      const roundedQuantity = Math.max(
        Math.min(
          Math.floor(maxAvailableUnits / unitMultiplier) * unitMultiplier,
          Math.round(quantity / unitMultiplier) * unitMultiplier
        ),
        minimumSelectableQuantity
      );
      return roundedQuantity.toString();
    },
    [maxAvailableUnits, minimumSelectableQuantity, unitMultiplier]
  );

  const productQuantityCrossoverPoint = useMemo(() => {
    return (
      unitMultiplier * (NUMBER_OF_QUANTITY_OPTIONS - 1) +
      minimumSelectableQuantity
    );
  }, [unitMultiplier, minimumSelectableQuantity]);

  const productQuantityOptions = useMemo(() => {
    const quantities = [];
    const initialQuantity =
      updateQuantityOnChange && productOptionInCart
        ? 0
        : minimumSelectableQuantity;
    for (
      let quantity = initialQuantity;
      quantity <= productQuantityCrossoverPoint &&
      quantity <= maxAvailableUnits;
      quantity += unitMultiplier
    ) {
      if (quantity !== 0 && quantity < minimumSelectableQuantity) {
        quantity = minimumSelectableQuantity;
      }

      quantities.push({
        label: productOptionQuantityLabel(quantity),
        value: quantity.toString(),
      });
    }
    return quantities;
  }, [
    maxAvailableUnits,
    minimumSelectableQuantity,
    productOptionInCart,
    productOptionQuantityLabel,
    productQuantityCrossoverPoint,
    unitMultiplier,
    updateQuantityOnChange,
  ]);

  const exceedMaxAvailableUnits = useMemo(() => {
    const isOpenSizingProduct =
      !!product.purchase_information?.per_style_min_order_quantity &&
      !isPrePackProductOption;

    if (isOpenSizingProduct) {
      return false;
    }

    return (
      (productOptionInCart?.quantity ?? 0) + selectedQuantity >
      maxAvailableUnits
    );
  }, [
    isPrePackProductOption,
    maxAvailableUnits,
    product.purchase_information?.per_style_min_order_quantity,
    productOptionInCart?.quantity,
    selectedQuantity,
  ]);

  /*
   *****************
   * Customization *
   *****************
   */

  const setCustomization = useCallback(
    (token: string, customization: string) => {
      if (customization === "") {
        setCustomizations((customizations) => {
          customizations.delete(token);
          return new Map(customizations);
        });
      } else {
        setCustomizations((customizations) => {
          customizations.set(token, customization);
          return new Map(customizations);
        });
      }

      if (updateQuantityOnChange) {
        debouncedCustomizationOnChange();
      }
    },
    [debouncedCustomizationOnChange, updateQuantityOnChange]
  );

  const getCustomization = useCallback(
    (token?: string) => {
      if (!token) {
        return "";
      }
      return customizations.get(token) || "";
    },
    [customizations]
  );

  const setCustomizationUsedInMoqCalculation = useCallback(
    (token: string, used: boolean) => {
      if (used) {
        setCustomizationsInMoqCalculation((customizationsInMoqCalculation) => {
          customizationsInMoqCalculation.add(token);
          return new Set(customizationsInMoqCalculation);
        });
      } else {
        setCustomizationsInMoqCalculation((customizationsInMoqCalculation) => {
          customizationsInMoqCalculation.delete(token);
          return new Set(customizationsInMoqCalculation);
        });
      }
    },
    []
  );

  const setPendingProductCustomizations = useCallback(
    (token: string, isUsed: boolean) => {
      setPendingCustomizations((prev) => {
        const existingEntry = [...prev].find(
          (item) => item.product_customization_token === token
        );

        if (isUsed && !existingEntry) {
          const newCustomization = IItemCustomization.build({
            product_customization_token: token,
          });
          return new Set([...prev, newCustomization]);
        } else if (!isUsed && existingEntry) {
          const newSet = new Set(prev);
          newSet.delete(existingEntry);
          return newSet;
        }
        return prev;
      });
    },
    []
  );

  /*
   *************
   * Prepacks *
   *************
   */

  const optionsByStyle = useMemo(() => {
    return groupBy(getSortedProductOptions(product), ({ variations }) =>
      getStyleKeyFromVariations(variations)
    );
  }, [product]);

  const currentStyleHasOnlyPrePacks = useMemo(() => {
    return getCurrentStyleHasOnlyPrePacks(optionsByStyle, selectedOption);
  }, [optionsByStyle, selectedOption]);

  /*
   ********
   * Misc *
   ********
   */

  const emailWhenBackInStock = useCallback(
    async (includeToast?: boolean) => {
      try {
        await subscribeBackInStock(selectedOption?.token ?? "");
        if (includeToast) {
          createToast(<BackInStockToast />, { timeout: "short" });
        }
      } catch {
        createToast(
          getErrorMessage(
            strictLocalize,
            ErrorMessages.PLEASE_REFRESH_AND_TRY_AGAIN
          ),
          { timeout: "medium", variant: "destructive" }
        );
      }
    },
    [selectedOption?.token, strictLocalize, subscribeBackInStock]
  );

  const deleteBackInStockNotification = useCallback(async () => {
    try {
      await unsubscribeBackInStock(selectedOption?.token ?? "");
    } catch {
      createToast(
        getErrorMessage(
          strictLocalize,
          ErrorMessages.PLEASE_REFRESH_AND_TRY_AGAIN
        ),
        { timeout: "medium", variant: "destructive" }
      );
    }
  }, [selectedOption?.token, strictLocalize, unsubscribeBackInStock]);

  useEffect(() => {
    const isRemovingFromCart =
      updateQuantityOnChange && productOptionInCart && selectedQuantity === 0;
    if (!isRemovingFromCart && selectedQuantity < minimumSelectableQuantity) {
      updateSelectedOptionQuantity(minimumSelectableQuantity);
    }
  }, [
    minimumSelectableQuantity,
    selectedQuantity,
    updateSelectedOptionQuantity,
    updateQuantityOnChange,
    productOptionInCart,
  ]);

  return useMemo(
    () => ({
      selectedOption,
      selectedQuantity,
      includesTesters,
      isSubscribedBackInStock,
      customizations,
      updateQuantityOnChange,
      customizationsInMoqCalculation,
      productAddOptions,
      updateSelectedOption,
      isOpenSizingVariant,
      showPreorderMessaging,
      isPreorderableSelected,
      perStyleOptionVariations,
      areAllOptionQuantitiesValid,
      openSizingShipDateRange,
      baseOrderByDate,
      openSizingAllSameDates,
      getIsUnderPerStyleMOQ,
      perStyleTotalQuantity,
      isSelectedOpenSizingQtyModified,
      updateSelectedQuantityByToken,
      isOptionPartOfOpenSizing,
      addOrUpdateSelectedOptionToInvoice,
      addOrUpdateSelectedOptionToCart,
      productOptionInCart,
      unitMultiplier,
      isPrePackProductOption,
      minimumOrderQuantity,
      productOptionQuantityLabel,
      selectedOptionLabel,
      productQuantityCrossoverPoint,
      productQuantityOptions,
      exceedMaxAvailableUnits,
      setSelectedQuantity,
      setCustomization,
      getCustomization,
      setCustomizationUsedInMoqCalculation,
      setPendingProductCustomizations,
      currentStyleHasOnlyPrePacks,
      emailWhenBackInStock,
      deleteBackInStockNotification,
      setIncludesTesters,
      minimumSelectableQuantity,
      maxAvailableUnits,
      getSelectedOptionInCart,
      hideProductDetails,
      setHideProductDetails,
      getCustomOptionValue,
      getExpectedAddToCartItems,
    }),
    [
      addOrUpdateSelectedOptionToCart,
      addOrUpdateSelectedOptionToInvoice,
      areAllOptionQuantitiesValid,
      baseOrderByDate,
      currentStyleHasOnlyPrePacks,
      customizations,
      customizationsInMoqCalculation,
      deleteBackInStockNotification,
      emailWhenBackInStock,
      exceedMaxAvailableUnits,
      getCustomization,
      isSubscribedBackInStock,
      includesTesters,
      isOpenSizingVariant,
      isOptionPartOfOpenSizing,
      isPrePackProductOption,
      isPreorderableSelected,
      isSelectedOpenSizingQtyModified,
      getIsUnderPerStyleMOQ,
      minimumOrderQuantity,
      openSizingAllSameDates,
      openSizingShipDateRange,
      perStyleOptionVariations,
      perStyleTotalQuantity,
      productAddOptions,
      productOptionInCart,
      productOptionQuantityLabel,
      productQuantityCrossoverPoint,
      productQuantityOptions,
      selectedOption,
      selectedOptionLabel,
      selectedQuantity,
      setCustomization,
      setCustomizationUsedInMoqCalculation,
      setPendingProductCustomizations,
      setSelectedQuantity,
      showPreorderMessaging,
      unitMultiplier,
      updateQuantityOnChange,
      updateSelectedOption,
      updateSelectedQuantityByToken,
      setIncludesTesters,
      minimumSelectableQuantity,
      maxAvailableUnits,
      getSelectedOptionInCart,
      hideProductDetails,
      setHideProductDetails,
      getCustomOptionValue,
      getExpectedAddToCartItems,
    ]
  );
};
